import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import "./CasePayment.css";
import Payment from "../../services/Payment";
import { useAuth } from "../../context/AuthContext";
import LoadingSpinner from "./LoadingSpinner";
import priceList from "../../priceList.json";
import FileUploadButton from "./FileUploadButton";
import {
  appendPaymentConfirmation,
  appendInvoice,
  removePaymentConfirmation,
  updateApplicationContent,
} from "../../services/Application";
import { commitStep } from "../../services/Process";
import { getPagesCount } from "../../services/Certificate";
import countriesEOGList from "../../countriesEOG.json";
import config from "../../config";
import "./CasePayment.css";


import logger from "../../utils/logger";
/**
 * Komponent do obsługi płatności za wnioski.
 *
 * @component
 * @param {String} status - stus płatności
 * @param {Object} applicationData - dane wniosku
 * @param {String} displayMode - tryb wyświetlania (domyślnie "Client")
 * @param {Boolean} disablePaymentButtons - flaga wyłączająca przyciski
 *
 * @returns {JSX.Element} - komponent płatności
 */
const CasePayment = ({
  status,
  applicationData,
  displayMode = "Client",
  disablePaymentButtons,
  editPaymentDetailsData,
  setEditPaymentDetailsData,
  applicationContent,
}) => {
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [prices, setPrices] = useState([]);
  const [countriesEOG] = useState(countriesEOGList);
  const [paymentDetails, setPaymentDetails] = useState({ Items: [] });
  const [loading, setLoading] = useState(false);
  const [showBankAccount, setShowBankAccount] = useState(false);
  const [paymentInProgress, setPaymentInProgress] = useState(false);
  const [invoiceInProgress, setInvoiceInProgress] = useState(false);
  const [invoiceFileId, setInvoiceFileId] = useState(
    applicationData.Invoice?.fileId
  );
  const [invoiceFileName, setInvoiceFileName] = useState(
    applicationData.Invoice?.fileName
  );
  const [receiverFromEOG, setReceiverFromEOG] = useState(null);
  const { accessToken, userId, userName, checkPermission } = useAuth();

  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentAttempted, setPaymentAttempted] = useState(false);
  const [paymentConfirmationUploaded, setPaymentConfirmationUploaded] =
    useState(false);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [estimatedCompletionTime, setEstimatedCompletionTime] = useState(null);
  const paymentCheckIntervalRef = useRef(null);

  const overdueTimeoutRef = useRef(null);

  const debug = config.runmode.debug;

  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        if (
          applicationData.PaymentConfirmation?.fileId ||
          applicationContent?.PaymentFileId
        ) {
          return;
        }

        const paymentStatus = await Payment.getStatus(
          applicationData.id,
          accessToken
        );

        if (paymentStatus) {
          const activeStatuses = ["NEW", "PENDING", "WAITING_FOR_CONFIRMATION"];

          if (activeStatuses.includes(paymentStatus.status)) {
            setPaymentProcessing(true);

            if (paymentStatus.created_at) {
              const elapsed = Date.now() - new Date(paymentStatus.created_at);
              const remaining = Math.max(0, 300000 - elapsed);

              setEstimatedCompletionTime(remaining);

              if (overdueTimeoutRef.current) {
                clearTimeout(overdueTimeoutRef.current);
              }
              overdueTimeoutRef.current = setTimeout(() => {
                setEstimatedCompletionTime(0);
              }, remaining);
            }

            if (!paymentCheckIntervalRef.current) {
              const interval = setInterval(async () => {
                const updatedStatus = await Payment.getStatus(
                  applicationData.id,
                  accessToken
                );

                if (updatedStatus.status === "COMPLETED") {
                  setPaymentProcessing(false);
                  window.location.reload();
                } else if (updatedStatus.status === "CANCELED") {
                  setPaymentProcessing(false);
                  setPaymentAttempted(true);
                  clearInterval(interval);
                  if (paymentCheckIntervalRef.current === interval) {
                    paymentCheckIntervalRef.current = null;
                  }
                }
              }, 10000);

              paymentCheckIntervalRef.current = interval;
            }
          } else if (paymentStatus.status === "CANCELED") {
            setPaymentAttempted(true);
          }
        }
      } catch (error) {
        logger.error("Error checking payment status:", error);
      }
    };

    checkPaymentStatus();

    return () => {
      if (paymentCheckIntervalRef.current) {
        clearInterval(paymentCheckIntervalRef.current);
        paymentCheckIntervalRef.current = null;
      }
      if (overdueTimeoutRef.current) {
        clearTimeout(overdueTimeoutRef.current);
      }
    };
  }, [applicationData.id, accessToken]);

  useEffect(() => {
    const content = applicationContent || applicationData;
    logger.log(content);
    if (content?.PaymentFileName && content?.PaymentFileId) {
      setPaymentConfirmationUploaded(true);
      setUploadedFileName(content?.PaymentFileName);
      setUploadedFileId(content?.PaymentFileId);
      return;
    }

    setPaymentConfirmationUploaded(false);
    setUploadedFileName(null);
    setUploadedFileId(null);
  }, [applicationContent, applicationData]);

  useEffect(() => {
    const invoiceId =
      applicationData?.Invoice?.fileId || applicationContent?.InvoiceFileId;
    const invoiceName =
      applicationData?.Invoice?.fileName || applicationContent?.InvoiceFileName;

    setInvoiceFileId(invoiceId || null);
    setInvoiceFileName(invoiceName || null);
  }, [applicationData, applicationContent]);

  const fetchPrices = async () => {
    try {
      setPrices(priceList);
    } catch (error) {
      logger.error("Błąd podczas ładowania cennika: ", error);
    }
  };

  const shouldRecalculatePaymentDetails = () => {
    const savedDetails = applicationContent?.PaymentDetails;
    if (!savedDetails) return true;

    return (
      savedDetails.country !== applicationData.paymentShippingCountry ||
      !savedDetails.calculatedAt ||
      applicationData.status === 6
    );
  };

  const savePaymentDetailsToContent = async (paymentDetails) => {
    try {
      const updatedContent = {
        ...applicationContent,
        PaymentDetails: paymentDetails,
      };

      await updateApplicationContent(
        applicationData.id,
        updatedContent,
        null,
        accessToken
      );
    } catch (error) {
      logger.error("Error saving payment details:", error);
    }
  };

  const fillPaymentDetails = async () => {
    let items = [];
    const override = applicationData.paymentOverride;
    if (override) {
      setPaymentDetails({
        Items: override.Items,
        total: override.total,
        totalGross: override.totalGross,
      });
      setEditPaymentDetailsData({
        comment: applicationData.paymentOverride.comment,
      });
      return;
    }

    if (
      applicationContent?.PaymentDetails &&
      !shouldRecalculatePaymentDetails()
    ) {
      const savedDetails = applicationContent.PaymentDetails;
      setPaymentDetails({
        Items: savedDetails.Items,
        total: savedDetails.total,
        totalGross: savedDetails.totalGross,
      });
      setReceiverFromEOG(savedDetails.receiverFromEOG);
      return;
    }

    const isReceiverFromEOG =
      countriesEOG.filter(
        (country) => country.code === applicationData.paymentShippingCountry
      ).length > 0;
    setReceiverFromEOG(isReceiverFromEOG);

    let total = 0;
    let totalGross = 0;

    const applicationProcessingFeeId =
      (await getPagesCount(applicationData.id, accessToken)) > 2 ? 2 : 1;

    const applicationProcessingFeeDetails = prices.filter(
      (price) => price.id === applicationProcessingFeeId
    )[0];

    if (debug)
      logger.log(
        "Szczegóły opłaty za obsługę wniosku: " +
          JSON.stringify(applicationProcessingFeeDetails)
      );
    if (debug) logger.log("Szczegóły wniosku: " + applicationData.name);

    if (applicationData.status === 6) {
      const applicationCancellationFeeDetails = prices.filter(
        (price) => price.id === 6
      )[0];
      items.push({
        name: applicationCancellationFeeDetails.fullName,
        price: applicationCancellationFeeDetails.priceNetto,
        quantity: 1,
        subtotal: applicationCancellationFeeDetails.priceNetto,
        taxRate: Math.round(
          ((isReceiverFromEOG
            ? applicationCancellationFeeDetails.priceBruttoEU
            : applicationCancellationFeeDetails.priceBruttoNonEU /
              applicationCancellationFeeDetails.priceNetto) -
            1) *
            100
        ),
        subtotalGross: isReceiverFromEOG
          ? applicationCancellationFeeDetails.priceBruttoEU
          : applicationCancellationFeeDetails.priceBruttoNonEU,
      });
      total += applicationCancellationFeeDetails.priceNetto;
      totalGross += isReceiverFromEOG
        ? applicationCancellationFeeDetails.priceBruttoEU
        : applicationCancellationFeeDetails.priceBruttoNonEU;
    } else {
      items.push({
        name: applicationProcessingFeeDetails.fullName,
        price: applicationProcessingFeeDetails.priceNetto,
        quantity: 1,
        subtotal: applicationProcessingFeeDetails.priceNetto,
        taxRate: Math.round(
          ((isReceiverFromEOG
            ? applicationProcessingFeeDetails.priceBruttoEU
            : applicationProcessingFeeDetails.priceBruttoNonEU) /
            applicationProcessingFeeDetails.priceNetto -
            1) *
            100
        ),
        subtotalGross: isReceiverFromEOG
          ? applicationProcessingFeeDetails.priceBruttoEU
          : applicationProcessingFeeDetails.priceBruttoNonEU,
      });
      total += applicationProcessingFeeDetails.priceNetto;
      totalGross += isReceiverFromEOG
        ? applicationProcessingFeeDetails.priceBruttoEU
        : applicationProcessingFeeDetails.priceBruttoNonEU;
      if (applicationData.paymentPrint) {
        let printFeeDetails = prices.filter((price) => price.id === 3)[0];
        if (debug)
          logger.log(
            "Szczegóły opłaty za wydruk: " + JSON.stringify(printFeeDetails)
          );
        items.push({
          name: printFeeDetails.fullName,
          price: printFeeDetails.priceNetto,
          quantity: 1,
          subtotal: printFeeDetails.priceNetto,
          taxRate: Math.round(
            ((isReceiverFromEOG
              ? printFeeDetails.priceBruttoEU
              : printFeeDetails.priceBruttoNonEU) /
              printFeeDetails.priceNetto -
              1) *
              100
          ),
          subtotalGross: isReceiverFromEOG
            ? printFeeDetails.priceBruttoEU
            : printFeeDetails.priceBruttoNonEU,
        });
        total += printFeeDetails.priceNetto;
        totalGross += isReceiverFromEOG
          ? printFeeDetails.priceBruttoEU
          : printFeeDetails.priceBruttoNonEU;
      }
      if (applicationData.paymentAdditionalPrints > 0) {
        let additionalPrintsFeeDetails = prices.filter(
          (price) => price.id === 4
        )[0];
        if (debug)
          logger.log(
            "Szczegóły opłaty za dodatkowe wydruki: " +
              JSON.stringify(additionalPrintsFeeDetails)
          );
        items.push({
          name: additionalPrintsFeeDetails.fullName,
          price: additionalPrintsFeeDetails.priceNetto,
          quantity: applicationData.paymentAdditionalPrints,
          subtotal:
            additionalPrintsFeeDetails.priceNetto *
            applicationData.paymentAdditionalPrints,
          taxRate: Math.round(
            ((isReceiverFromEOG
              ? additionalPrintsFeeDetails.priceBruttoEU
              : additionalPrintsFeeDetails.priceBruttoNonEU) /
              additionalPrintsFeeDetails.priceNetto -
              1) *
              100
          ),
          subtotalGross: isReceiverFromEOG
            ? additionalPrintsFeeDetails.priceBruttoEU *
              applicationData.paymentAdditionalPrints
            : additionalPrintsFeeDetails.priceBruttoNonEU *
              applicationData.paymentAdditionalPrints,
        });
        total +=
          additionalPrintsFeeDetails.priceNetto *
          applicationData.paymentAdditionalPrints;
        totalGross += isReceiverFromEOG
          ? additionalPrintsFeeDetails.priceBruttoEU *
            applicationData.paymentAdditionalPrints
          : additionalPrintsFeeDetails.priceBruttoNonEU *
            applicationData.paymentAdditionalPrints;
      }
      if (
        applicationData.paymentShipping === "courier" &&
        [true, "true"].includes(applicationData.paymentPrint)
      ) {
        let shippingFeeDetails = prices.filter((price) => price.id === 5)[0];
        if (debug)
          logger.log(
            "Szczegóły opłaty za dostawę: " + JSON.stringify(shippingFeeDetails)
          );
        items.push({
          name: shippingFeeDetails.fullName,
          price: shippingFeeDetails.priceNetto,
          quantity: 1,
          subtotal: shippingFeeDetails.priceNetto,
          taxRate: Math.round(
            ((isReceiverFromEOG
              ? shippingFeeDetails.priceBruttoEU
              : shippingFeeDetails.priceBruttoNonEU) /
              shippingFeeDetails.priceNetto -
              1) *
              100
          ),
          subtotalGross: isReceiverFromEOG
            ? shippingFeeDetails.priceBruttoEU
            : shippingFeeDetails.priceBruttoNonEU,
        });
        total += shippingFeeDetails.priceNetto;
        totalGross += isReceiverFromEOG
          ? shippingFeeDetails.priceBruttoEU
          : shippingFeeDetails.priceBruttoNonEU;
      }
    }

    const calculatedPaymentDetails = {
      Items: items,
      total: total,
      totalGross: totalGross,
      calculatedAt: new Date().toISOString(),
      receiverFromEOG: isReceiverFromEOG,
      country: applicationData.paymentShippingCountry,
    };

    setPaymentDetails(calculatedPaymentDetails);

    if (!applicationContent?.PaymentDetails) {
      await savePaymentDetailsToContent(calculatedPaymentDetails);
    }
  };

  const formatOperatorName = (name) => {
    if (!name || name.length == 0) return;
    const split = name.trim().split(" ");
    return `${split[1]} ${split[0]}`;
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case "paid":
        return applicationData.paymentMethod === "Jednorazowa"
          ? "Wniosek opłacony"
          : "Płatność odroczona";
      case "pending":
        return "Wniosek nieopłacony";
      case "failed":
        return "Płatność nie powiodła się.";
      case "toedit":
        return "Wniosek nieopłacony";
      default:
        return "Nieznany status płatności.";
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("pl-PL", {
      style: "currency",
      currency: "PLN",
    }).format(amount);
  };

  /**
   * Funkcja przetwarzająca płatność online.
   *
   * @function
   */
  const handlePayment = async () => {
    if (paymentConfirmationUploaded) {
      alert(
        "Usuń najpierw potwierdzenie przelewu tradycyjnego przed opłaceniem online."
      );
      return;
    }

    setPaymentInProgress(true);

    const plnString = paymentDetails.totalGross.toFixed(2);
    const grossString = plnString.replace(".", "");
    const amountGross = parseInt(grossString, 10);

    try {
      const result = await Payment.order(
        applicationData.id,
        userId,
        amountGross,
        applicationData.number,
        accessToken
      );

      if (result) {
        window.location.href = result;
      } else if (result.message === "Order already exists") {
        window.location.href = result;
      }
    } catch (error) {
      logger.error("Payment error:", error);
      setPaymentAttempted(true);
    } finally {
      setPaymentInProgress(false);
    }
  };

  const handleRemovePaymentConfirmation = async () => {
    try {
      await removePaymentConfirmation(applicationData.id, accessToken);
      setPaymentConfirmationUploaded(false);
      setUploadedFileName(null);
      setUploadedFileId(null);

      window.location.reload();
    } catch (error) {
      logger.error("Error removing payment confirmation:", error);
      alert("Błąd podczas usuwania potwierdzenia płatności");
    }
  };

  const getPaymentStatusMessage = () => {
    const isOperator = checkPermission("Zmiana przypisania sprawy");
    const paymentFinalized =
      status === "paid" ||
      paymentConfirmationUploaded ||
      applicationData.PaymentConfirmation?.fileId ||
      applicationContent?.PaymentFileId;

    if (paymentFinalized) {
      return null;
    }

    if (paymentProcessing && estimatedCompletionTime != null) {
      const minutes = Math.ceil(estimatedCompletionTime / 60000);

      if (isOperator) {
        if (minutes > 0) {
          return `Płatność w trakcie realizacji. Szacowany czas zakończenia procesu to ${minutes} minut.`;
        } else {
          return "Proces płatności przekroczył przewidywany czas. Możliwe opóźnienia po stronie operatora płatności.";
        }
      } else {
        if (minutes > 0) {
          return `Płatność jest w trakcie realizacji. Weryfikacja zostanie zakończona w ciągu ${minutes} minut.`;
        } else {
          return "Weryfikacja płatności trwa dłużej niż zwykle. Jeśli problem się utrzymuje, skontaktuj się z obsługą.";
        }
      }
    }

    if (paymentAttempted && !paymentProcessing) {
      return isOperator
        ? "Poprzednia próba płatności nie powiodła się. Wnioskodawca może spróbować ponownie lub wybrać płatność przelewem tradycyjnym."
        : "Poprzednia próba płatności nie powiodła się. Możesz spróbować ponownie lub wybrać przelew tradycyjny.";
    }

    return null;
  };

  const performCommit = async () => {
    try {
      await commitStep(applicationData.id, userName, accessToken);
      window.location.reload();
    } catch (error) {
      logger.error("Error committing step:", error);
    }
  };

  const handlePaymentConfirmation = async (paymentFileId, paymentFileName) => {
    if (paymentProcessing) {
      alert(
        "Płatność online jest w trakcie realizacji. Poczekaj na jej zakończenie."
      );
      return;
    }

    setPaymentInProgress(true);

    const plnString = paymentDetails.totalGross.toFixed(2);
    const grossString = plnString.replace(".", "");
    const amountGross = parseInt(grossString, 10);

    try {
      await appendPaymentConfirmation(
        applicationData.id,
        paymentFileId,
        paymentFileName,
        amountGross,
        accessToken
      );

      setPaymentConfirmationUploaded(true);
      setUploadedFileName(paymentFileName);
      setUploadedFileId(paymentFileId);

      await performCommit();
    } catch (error) {
      logger.error("Error uploading payment confirmation:", error);
    } finally {
      setPaymentInProgress(false);
    }
  };

  const handleInvoice = async (invoiceFileId, invoiceFileName) => {
    setInvoiceInProgress(true);
    await appendInvoice(
      applicationData.id,
      invoiceFileId,
      invoiceFileName,
      accessToken
    );
    setInvoiceFileId(invoiceFileId);
    setInvoiceFileName(invoiceFileName);
    setInvoiceInProgress(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        if (
          status === "pending" ||
          (status === "toedit" && checkPermission("Zmiana przypisania sprawy"))
        ) {
          setShowPaymentDetails(true);
        }
        await fetchPrices();
      } catch (error) {
        logger.error("Failed to fetch payment details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [status]);

  useEffect(() => {
    if (prices.length > 0 && countriesEOG.length > 0) {
      fillPaymentDetails();
    }
  }, [prices, countriesEOG, applicationData, accessToken]);

  useEffect(() => {
    if (
      !editPaymentDetailsData.editMode &&
      prices.length > 0 &&
      countriesEOG.length > 0
    ) {
      setEditPaymentDetailsData({ editMode: false, comment: null, data: null });
      fillPaymentDetails();
    }
  }, [editPaymentDetailsData.editMode, priceList, countriesEOG]);

  if (displayMode === "Shipping") {
    return null;
  }

  return (
    <div className="border-top pt-1 mt-1">
      <div className="w-100 d-flex flex-row align-items-center">
        <div>
          <i
            className={`fs-1 bi bi-${
              status === "paid"
                ? "check-circle-fill text-success"
                : "x-circle-fill text-danger"
            }`}
          />
        </div>

        <div className="ms-1">
          <strong>{getStatusMessage(status)}</strong>
          <br />
          {status === "paid" && (
            <span className="text-black-50">
              {applicationData.paymentMethod === "Jednorazowa"
                ? "Zapłacono"
                : "Naliczono"}{" "}
              (brutto): {formatCurrency(paymentDetails.totalGross)}
            </span>
          )}
        </div>

        {status === "paid" && (
          <div className="d-flex flex-row ms-auto">
            {invoiceInProgress ? (
              <div className="btn btn-primary me-1 my-0 h2 w7" role="status">
                <LoadingSpinner text="Dodawanie" />
              </div>
            ) : (
              displayMode === "Operator" && (
                <FileUploadButton
                  className="btn btn-primary me-1 h2 w7"
                  text="Dodaj fakturę"
                  onChange={handleInvoice}
                />
              )
            )}
            {invoiceFileId && (
              <FileUploadButton
                className="btn btn-primary me-1 my-0 h2 w11"
                fileId={invoiceFileId}
                fileName={invoiceFileName}
                renderAsLink={false}
                downloadText="Pobierz fakturę"
              />
            )}
            <button
              className="btn btn-outline-primary w11 h2"
              onClick={() => setShowPaymentDetails(!showPaymentDetails)}
            >
              {showPaymentDetails ? "Ukryj" : "Pokaż"} szczegóły płatności
            </button>
          </div>
        )}
      </div>

      {getPaymentStatusMessage() && (
        <div
          className={`alert ${
            paymentProcessing ? "alert-info" : "alert-warning"
          } mt-2`}
        >
          <i
            className={`bi bi-${
              paymentProcessing ? "clock-history" : "exclamation-triangle"
            } me-2`}
          ></i>
          {getPaymentStatusMessage()}
        </div>
      )}

      {!loading && paymentDetails && showPaymentDetails && (
        <div className="pt-1">
          <strong>
            Szczegóły płatności - kraj docelowy{" "}
            {receiverFromEOG
              ? "z UE"
              : "spoza UE (" + applicationData.paymentShippingCountry + ")"}
          </strong>
          <div className="d-flex flex-column mt-1">
            <div className="d-flex flex-row fw-semibold text-end">
              <div className="w15 text-start">
                <span>Usługa</span>
              </div>
              <div className="w5 ms-1">
                <span>Ilość</span>
              </div>
              <div className="w5 ms-1">
                <span>Cena</span>
              </div>
              <div className="w7 ms-1">
                <span>Suma (netto)</span>
              </div>
              <div className="w3 ms-1">
                <span>VAT</span>
              </div>
              <div className="w7 ms-1">
                <span>Suma (brutto)</span>
              </div>
            </div>
            {paymentDetails &&
              paymentDetails.Items.length > 0 &&
              paymentDetails.Items.map((item, idx) => (
                <div
                  key={idx}
                  className={`d-flex flex-row text-end text-black-50  ${
                    editPaymentDetailsData.editMode
                      ? "edit-payment-details"
                      : ""
                  } ${idx % 2 === 0 ? "bg-light bg-opacity-50" : ""}`}
                >
                  <div className="w15 text-start">
                    {editPaymentDetailsData.editMode ? (
                      <textarea
                        ref={(el) => {
                          if (!el) return;
                          el.style.height = "auto";
                          el.style.height = el.scrollHeight + "px";
                        }}
                        rows={1}
                        className="form-control"
                        style={{ overflow: "hidden", resize: "none" }}
                        value={editPaymentDetailsData.data.Items[idx].name}
                        onChange={(e) => {
                          const val = e.target.value;
                          const d = { ...editPaymentDetailsData.data };
                          d.Items = d.Items.map((it, i) =>
                            i === idx ? { ...it, name: val } : it
                          );
                          setEditPaymentDetailsData((prev) => ({
                            ...prev,
                            data: d,
                          }));
                        }}
                      />
                    ) : (
                      <span className="text-black">{item.name}</span>
                    )}
                  </div>

                  <div className="w5 ms-1">
                    {editPaymentDetailsData.editMode ? (
                      <input
                        type="number"
                        className="form-control no-spin"
                        value={editPaymentDetailsData.data.Items[idx].quantity}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10) || 0;
                          const d = { ...editPaymentDetailsData.data };
                          d.Items[idx].quantity = val;
                          d.Items[idx].subtotal = val * d.Items[idx].price;
                          d.Items[idx].subtotalGross =
                            val *
                            d.Items[idx].price *
                            (1 + d.Items[idx].taxRate / 100);
                          d.total = d.Items.reduce((s, i) => s + i.subtotal, 0);
                          d.totalGross = d.Items.reduce(
                            (s, i) => s + i.subtotalGross,
                            0
                          );
                          setEditPaymentDetailsData((prev) => ({
                            ...prev,
                            data: d,
                          }));
                        }}
                      />
                    ) : (
                      <span>{item.quantity}</span>
                    )}
                  </div>
                  <div className="w5 ms-1">
                    {editPaymentDetailsData.editMode ? (
                      <input
                        type="number"
                        className="form-control no-spin"
                        step="0.01"
                        value={editPaymentDetailsData.data.Items[idx].price}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          const d = { ...editPaymentDetailsData.data };
                          d.Items[idx].price = val;
                          d.Items[idx].subtotal = d.Items[idx].quantity * val;
                          d.Items[idx].subtotalGross =
                            d.Items[idx].quantity *
                            val *
                            (1 + d.Items[idx].taxRate / 100);
                          d.total = d.Items.reduce((s, i) => s + i.subtotal, 0);
                          d.totalGross = d.Items.reduce(
                            (s, i) => s + i.subtotalGross,
                            0
                          );
                          setEditPaymentDetailsData((prev) => ({
                            ...prev,
                            data: d,
                          }));
                        }}
                      />
                    ) : (
                      <span>{formatCurrency(item.price)}</span>
                    )}
                  </div>
                  <div className="w7 ms-1">{formatCurrency(item.subtotal)}</div>
                  <div className="w3 ms-1">
                    {editPaymentDetailsData.editMode ? (
                      <input
                        type="number"
                        className="form-control no-spin"
                        step="0.1"
                        value={editPaymentDetailsData.data.Items[idx].taxRate}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          const d = { ...editPaymentDetailsData.data };
                          d.Items[idx].taxRate = val;
                          d.Items[idx].subtotalGross =
                            d.Items[idx].quantity *
                            d.Items[idx].price *
                            (1 + val / 100);
                          d.totalGross = d.Items.reduce(
                            (s, i) => s + i.subtotalGross,
                            0
                          );
                          setEditPaymentDetailsData((prev) => ({
                            ...prev,
                            data: d,
                          }));
                        }}
                      />
                    ) : (
                      <span>{item.taxRate}%</span>
                    )}
                  </div>
                  <div className="w7 ms-1 text-black">
                    {formatCurrency(item.subtotalGross)}
                  </div>
                </div>
              ))}
          </div>
          <div className="d-flex flex-row mt-1">
            <div className="w27">Łączna kwota</div>
            <div className="w7 ms-1 text-end">
              <strong>
                {editPaymentDetailsData.editMode
                  ? formatCurrency(editPaymentDetailsData.data.total)
                  : formatCurrency(paymentDetails.total)}
              </strong>
            </div>
            <div className="w7 ms-5 text-end">
              <strong>
                {editPaymentDetailsData.editMode
                  ? formatCurrency(editPaymentDetailsData.data.totalGross)
                  : formatCurrency(paymentDetails.totalGross)}
              </strong>
            </div>
          </div>
          {status == "toedit" &&
            checkPermission("Zmiana przypisania sprawy") && (
              <div className="mt-2">
                <div>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="details_editmode"
                    checked={editPaymentDetailsData.editMode}
                    onChange={() => {
                      const newMode = !editPaymentDetailsData.editMode;
                      setEditPaymentDetailsData({
                        editMode: newMode,
                        comment: newMode
                          ? editPaymentDetailsData.comment
                          : null,
                        data: newMode
                          ? editPaymentDetailsData.data || {
                              Items: paymentDetails.Items,
                              total: paymentDetails.total,
                              totalGross: paymentDetails.totalGross,
                            }
                          : null,
                      });
                    }}
                  />

                  <label
                    className="form-check-label"
                    htmlFor={"details_editmode"}
                  >
                    <strong>Chcę edytować szczegóły płatności</strong>
                  </label>
                </div>
                {editPaymentDetailsData.editMode && (
                  <textarea
                    className="form-control h8 rounded-3 mt-1"
                    maxLength="3600"
                    name="details_edit_comment"
                    value={editPaymentDetailsData.comment}
                    onChange={(e) =>
                      setEditPaymentDetailsData((prev) => ({
                        editMode: prev.editMode,
                        comment: e.target.value,
                        data: prev.data,
                      }))
                    }
                    placeholder="Komentarz*"
                    required={true}
                  />
                )}
              </div>
            )}

          {!paymentInProgress &&
            applicationData.PaymentConfirmation &&
            applicationData.PaymentConfirmation.fileId &&
            applicationData.PaymentConfirmation.fileName && (
              <div className="w-100 d-flex flex-row mt-1">
                <span className="w9">
                  <strong>Potwierdzenie opłaty</strong>
                </span>
                <FileUploadButton
                  className="btn btn-primary ms-1 my-0 h2 w11"
                  fileId={applicationData.PaymentConfirmation.fileId}
                  fileName={applicationData.PaymentConfirmation.fileName}
                  onRemove={handleRemovePaymentConfirmation}
                />
              </div>
            )}

          {status == "paid" && applicationData?.paymentOverride?.comment && (
            <div className="mt-1 w-100">
              Edycja kwoty przez {formatOperatorName(applicationData.operator)}:{" "}
              {applicationData.paymentOverride.comment}
            </div>
          )}

          {status == "paid" && !applicationData.PaymentConfirmation?.fileId && (
            <div className="w-100 d-flex flex-row mt-1">
              <span className="w9">
                <strong>Zapłacono online</strong>
              </span>
            </div>
          )}
        </div>
      )}

      {status === "pending" && displayMode === "Client" && (
        <div className="w-100 d-flex flex-row mt-2">
          {paymentInProgress ? (
            <button
              className="btn btn-primary ms-auto my-0 h2 w11"
              type="button"
              disabled
            >
              <LoadingSpinner text="Przetwarzanie" />
            </button>
          ) : (
            <div className="ms-auto">
              <div className="btn-group">
                <button
                  className={`btn btn-primary my-0 h2 w11 tooltip ${
                    paymentConfirmationUploaded || paymentProcessing
                      ? "disabled"
                      : ""
                  }`}
                  type="button"
                  onClick={handlePayment}
                  disabled={paymentConfirmationUploaded || paymentProcessing}
                >
                  Opłać online
                  {(paymentConfirmationUploaded || paymentProcessing) && (
                    <span className="tooltiptext">
                      {paymentConfirmationUploaded && !paymentProcessing
                        ? "Najpierw usuń potwierdzenie opłaty przelewem"
                        : "Płatność w toku"}
                    </span>
                  )}
                </button>
                <button
                  className={`btn btn-outline-primary ms-1 my-0 h2 w11 tooltip ${
                    paymentProcessing ? "disabled" : ""
                  }`}
                  type="button"
                  onClick={() => setShowBankAccount(!showBankAccount)}
                  disabled={paymentProcessing}
                >
                  Przelew tradycyjny
                  {paymentProcessing && (
                    <span className="tooltiptext">Płatność w toku</span>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {showBankAccount && (
        <div className="mt-2">
          <div className="d-flex flex-row mt-1">
            <div className="w-100">
              <h4>Dane do przelewu</h4>
            </div>
          </div>

          <div className="d-flex flex-row mt-1">
            <div className="w7">
              <strong>Kwota</strong>
            </div>
            <div className="w25">
              <span>{formatCurrency(paymentDetails.totalGross)}</span>
              <button
                className="btn ms-1 h1 p-0 tooltip"
                onClick={() =>
                  navigator.clipboard.writeText(paymentDetails.totalGross)
                }
              >
                <span className="bi bi-copy"></span>
                <span className="tooltiptext">Skopiuj kwotę</span>
              </button>
            </div>
          </div>

          <div className="d-flex flex-row mt-1">
            <div className="w7">
              <strong>Odbiorca</strong>
            </div>
            <div className="w25">
              <span>Krajowa Izba Gospodarcza BLCA</span>
              <button
                className="btn ms-1 h1 p-0 tooltip"
                onClick={() =>
                  navigator.clipboard.writeText("Krajowa Izba Gospodarcza BLCA")
                }
              >
                <span className="bi bi-copy"></span>
                <span className="tooltiptext">Skopiuj odbiorcę</span>
              </button>
            </div>
          </div>

          <div className="d-flex flex-row mt-1">
            <div className="w7">
              <strong>Numer konta</strong>
            </div>
            <div className="w25">
              <span>PL 30 1160 2202 0000 0006 5853 7629</span>
              <button
                className="btn ms-1 h1 p-0 tooltip"
                onClick={() =>
                  navigator.clipboard.writeText(
                    "PL 30 1160 2202 0000 0006 5853 7629"
                  )
                }
              >
                <span className="bi bi-copy"></span>
                <span className="tooltiptext">Skopiuj numer konta</span>
              </button>
            </div>
          </div>

          <div className="d-flex flex-row mt-1">
            <div className="w7">
              <strong>Tytuł</strong>
            </div>
            <div className="w25">
              <span>Wniosek nr {applicationData.number}</span>
              <button
                className="btn ms-1 h1 p-0 tooltip"
                onClick={() =>
                  navigator.clipboard.writeText(
                    "Wniosek nr " + applicationData.number
                  )
                }
              >
                <span className="bi bi-copy"></span>
                <span className="tooltiptext">Skopiuj tytuł przelewu</span>
              </button>
            </div>
          </div>

          <div className="d-flex flex-row mt-1">
            <span>
              Możesz przyśpieszyć weryfikację płatności poprzez dołączenie
              potwierdzenia przelewu.
            </span>
            <div className="ms-auto">
              {!paymentInProgress ? (
                paymentConfirmationUploaded ? (
                  <button className="btn btn-success my-0 h2 w11" disabled>
                    <i className="bi bi-check-circle"></i> Dodano
                  </button>
                ) : (
                  <FileUploadButton
                    className="btn btn-primary my-0 h2 w11"
                    text="Dodaj potwierdzenie"
                    onChange={handlePaymentConfirmation}
                    onRemove={handleRemovePaymentConfirmation}
                    disabled={paymentProcessing}
                  />
                )
              ) : (
                <div className="btn btn-primary my-0 h2 w11" role="status">
                  <LoadingSpinner text="Przetwarzanie" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

CasePayment.propTypes = {
  status: PropTypes.oneOf(["paid", "pending", "failed"]).isRequired,
};

export default CasePayment;


